import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CheckCircle, ArrowRight, Zap, Users, ShieldCheck, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }),
};

const DOMAINS = [
  "Préparation de commandes",
  "Manutention et agents logistiques",
  "Responsable exploitation",
  "Caristes",
  "Gestion de stocks",
  "Gestion administrative",
];

const ENGAGEMENTS = [
  { icon: Zap, title: "Réactivité", desc: "Mise à disposition rapide des profils qualifiés" },
  { icon: ShieldCheck, title: "Sélection rigoureuse", desc: "Candidats évalués et validés" },
  { icon: Users, title: "Expertise terrain", desc: "Compréhension fine des métiers logistiques" },
  { icon: Clock, title: "Flexibilité", desc: "Adaptation à vos pics d'activité" },
];

export default function EntreprisePage() {
  return (
    <main data-testid="entreprise-page">
      <SEO
        title="Entreprise - Votre partenaire logistique en intérim"
        description="FR INTERIM accompagne les entreprises dans leurs besoins en recrutement intérim, CDD et CDI dans la logistique et supply chain. Réactivité, sélection rigoureuse, expertise terrain."
        path="/entreprise"
      />
      {/* Hero */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://static.prod-images.emergentagent.com/jobs/c50239db-e303-4ac2-9fad-ed65c9848429/images/155999c82db13eb733ce3c86ed9c87c73b3e7b71f45a289bf1f0c2647a679049.png')" }} />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" className="max-w-2xl">
            <motion.p variants={fadeUp} custom={0} className="text-[#E5CD96] text-xs font-bold uppercase tracking-[0.2em] mb-4">Espace Entreprise</motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white mb-6">
              Votre partenaire logistique en intérim
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-lg text-gray-200 leading-relaxed">
              FR INTERIM est une agence de travail temporaire spécialisée dans les métiers de la logistique
              et de la supply chain. Nous accompagnons les entreprises dans leurs besoins en recrutements
              en intérim, CDD et CDI.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Description */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="max-w-3xl mx-auto text-center mb-16">
            <motion.p variants={fadeUp} custom={0} className="text-base md:text-lg text-gray-600 leading-relaxed">
              Dans un secteur où la réactivité est essentielle, nous vous apportons des solutions rapides,
              fiables et adaptées à vos enjeux opérationnels.
            </motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.p variants={fadeUp} custom={0} className="text-xs font-bold uppercase tracking-[0.2em] text-[#662483] mb-3 text-center">Nos domaines</motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#3F3F3F] mb-10 text-center">
              Nos domaines d'intervention
            </motion.h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {DOMAINS.map((domain, i) => (
              <motion.div key={domain} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeUp} className="flex items-center gap-3 bg-[#FAFAFA] border border-gray-200 p-5">
                <CheckCircle className="w-5 h-5 text-[#0069B4] shrink-0" />
                <span className="text-sm font-medium text-[#3F3F3F]">{domain}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement */}
      <section className="py-16 md:py-24 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-bold uppercase tracking-[0.2em] text-[#662483] mb-3">Notre engagement</motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#3F3F3F]">
              Pourquoi choisir FR INTERIM ?
            </motion.h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-6">
            {ENGAGEMENTS.map((item, i) => (
              <motion.div key={item.title} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeUp} className="bg-white border border-gray-200 p-6 md:p-8 card-hover">
                <div className="w-12 h-12 bg-gradient-to-r from-[#662483] to-[#0069B4] flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-[#3F3F3F] mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="brand-gradient py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">Sécurisez vos opérations avec FR INTERIM</h2>
          <p className="text-lg text-white/80 mb-8">Un partenaire capable de renforcer vos équipes rapidement.</p>
          <Link to="/contact">
            <Button className="bg-[#E5CD96] text-[#3F3F3F] hover:bg-[#B8A478] font-semibold px-8 py-3 rounded-none text-base" data-testid="entreprise-cta">
              Nous contacter <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
}
